import { formatLocalDate, smartSearch } from "../lib/utils";
import { useState, useEffect } from "react";
import { useNavigate, useNavigate as useRouterNavigate } from "react-router-dom";;
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Calendar,
  CalendarDays,
  Clock,
  Phone,
  User,
  Smartphone,
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Truck,
  Eye,
  MessageCircle,
  Search
} from "lucide-react";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate WhatsApp link for reminders
const getWhatsAppLink = (phone, clientName, date, timeSlot, type) => {
  // Clean phone number (remove spaces, keep only digits)
  let cleanPhone = phone?.replace(/\s/g, '').replace(/^0/, '596'); // Martinique country code
  if (!cleanPhone?.startsWith('596')) {
    cleanPhone = '596' + cleanPhone?.replace(/^0/, '');
  }
  
  const typeLabel = type === 'intake' ? 'dépôt de votre appareil' : 'récupération de votre appareil';
  const message = encodeURIComponent(
    `Bonjour ${clientName} ! 👋\n\n` +
    `Ceci est un rappel pour votre RDV de ${typeLabel} prévu le ${date} à ${timeSlot}.\n\n` +
    `📍 ${shopSettings?.shop_name || "AMCPhone Martinique"}\n\n` +
    `À bientôt ! 😊`
  );
  
  return `https://wa.me/${cleanPhone}?text=${message}`;
};

const DAYS_OF_WEEK = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const DAYS_OF_WEEK_FULL = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const STATUS_CONFIG = {
  pending:   { label: "À confirmer",   color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  confirmed: { label: "Confirmé",      color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  cancelled: { label: "Annulé",        color: "bg-red-500/20 text-red-400 border-red-500/30" },
  completed: { label: "Terminé ✓",     color: "bg-teal-500/20 text-teal-400 border-teal-500/30" },
  converted: { label: "En réparation", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" }
};

const TYPE_CONFIG = {
  intake: { label: "Prise en charge", icon: Inbox, color: "text-cyan-500" },
  pickup: { label: "Récupération", icon: Truck, color: "text-emerald-500" }
};

// Generate time slots from 9h to 17h
// Créneaux calendrier admin : 6h–22h pour afficher les RDV hors horaires
const generateTimeSlots = () => {
  const slots = [];
  for (let h = 6; h < 22; h++) {
    for (let m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
};
const TIME_SLOTS = generateTimeSlots();
// Rétrocompat legacy
const fmtSlot = (v) => ({ morning:"Matin (9h–12h)", afternoon:"Après-midi (14h–17h)", evening:"Fin de journée (17h–19h)" }[v] || v || "—");

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // "month", "week", "day"
  const [filter, setFilter] = useState("all"); // "all", "pending", "confirmed"
  const [typeFilter, setTypeFilter] = useState("all"); // "all", "intake", "pickup"
  const [searchQuery, setSearchQuery] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const routerNavigate = useRouterNavigate();

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/admin/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des rendez-vous");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointment, newStatus) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/admin/appointments/${appointment.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rendez-vous ${newStatus === 'confirmed' ? 'confirmé' : newStatus === 'cancelled' ? 'annulé' : 'mis à jour'}`);
      setDetailDialogOpen(false);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setUpdating(false);
    }
  };

  // Filter appointments
  const filteredAppointments = appointments.filter(apt => {
    const matchesStatus = filter === "all" || apt.status === filter;
    const matchesType = typeFilter === "all" || apt.type === typeFilter;
    const matchesSearch = smartSearch(searchQuery, [
      apt.client_name, apt.client_phone, apt.device_brand, apt.device_model, apt.ticket_number
    ]);
    return matchesStatus && matchesType && matchesSearch;
  });

  // Count by status
  const counts = {
    all: appointments.length,
    pending: appointments.filter(a => a.status === "pending").length,
    confirmed: appointments.filter(a => a.status === "confirmed").length
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { 
      daysInMonth: lastDay.getDate(), 
      startingDay: firstDay.getDay(),
      year,
      month
    };
  };

  const getWeekDates = (date) => {
    const day = date.getDay();
    const diff = date.getDate() - day;
    const weekStart = new Date(date.getFullYear(), date.getMonth(), diff);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i));
    }
    return dates;
  };

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getAppointmentsForDate = (dateKey) => {
    return filteredAppointments.filter(apt => apt.preferred_date === dateKey);
  };

  const getAppointmentsForSlot = (dateKey, timeSlot) => {
    return filteredAppointments.filter(apt => apt.preferred_date === dateKey && apt.preferred_time_slot === timeSlot);
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setDate(newDate.getDate() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const openDetail = (apt) => {
    setSelectedAppointment(apt);
    setDetailDialogOpen(true);
  };

  // Render appointment badge
  const renderAppointmentBadge = (apt, compact = false) => {
    const TypeIcon = TYPE_CONFIG[apt.type]?.icon || Calendar;
    const typeColor = TYPE_CONFIG[apt.type]?.color || "text-gray-500";
    const statusConfig = STATUS_CONFIG[apt.status] || STATUS_CONFIG.pending;
    
    if (compact) {
      return (
        <div 
          key={apt.id}
          onClick={() => openDetail(apt)}
          className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 ${statusConfig.color}`}
        >
          <TypeIcon className={`w-3 h-3 ${typeColor}`} />
          <span>{apt.preferred_time_slot}</span>
        </div>
      );
    }
    
    return (
      <div 
        key={apt.id}
        onClick={() => openDetail(apt)}
        className={`p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all border ${statusConfig.color}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <TypeIcon className={`w-4 h-4 ${typeColor}`} />
          <span className="text-xs font-medium">{TYPE_CONFIG[apt.type]?.label}</span>
        </div>
        <p className="text-sm font-medium truncate">{apt.client_name}</p>
        <p className="text-xs text-muted-foreground">{apt.preferred_time_slot}</p>
      </div>
    );
  };

  // Month View
  const renderMonthView = () => {
    const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentDate);
    const days = [];

    // Empty cells
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-28 border border-border/30 bg-secondary/10"></div>);
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateKey = formatDateKey(date);
      const dayAppointments = getAppointmentsForDate(dateKey);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <div 
          key={day} 
          className={`h-28 border border-border/30 p-1 overflow-hidden
            ${isWeekend ? 'bg-red-500/5' : 'bg-card/50'}
            ${isToday ? 'ring-2 ring-primary ring-inset' : ''}
          `}
        >
          <div className={`text-xs font-medium mb-1 ${isWeekend ? 'text-red-400/50' : 'text-muted-foreground'}`}>
            {day}
          </div>
          <div className="space-y-0.5 overflow-y-auto max-h-20">
            {dayAppointments.slice(0, 3).map(apt => renderAppointmentBadge(apt, true))}
            {dayAppointments.length > 3 && (
              <div className="text-[10px] text-muted-foreground text-center">
                +{dayAppointments.length - 3} autres
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-7 gap-0">
        {DAYS_OF_WEEK.map(day => (
          <div key={day} className={`h-8 flex items-center justify-center text-xs font-medium border border-border/30 bg-secondary/30 ${day === 'Dim' || day === 'Sam' ? 'text-red-400' : 'text-muted-foreground'}`}>
            {day}
          </div>
        ))}
        {days}
      </div>
    );
  };

  // Week View
  const renderWeekView = () => {
    const weekDates = getWeekDates(currentDate);

    return (
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Header */}
          <div className="grid grid-cols-8 gap-0">
            <div className="h-12 border border-border/30 bg-secondary/30"></div>
            {weekDates.map((date, idx) => {
              const isToday = new Date().toDateString() === date.toDateString();
              const isWeekend = idx === 0 || idx === 6;
              return (
                <div key={idx} className={`h-12 border border-border/30 bg-secondary/30 flex flex-col items-center justify-center ${isToday ? 'bg-primary/20' : ''}`}>
                  <span className={`text-xs ${isWeekend ? 'text-red-400' : 'text-muted-foreground'}`}>{DAYS_OF_WEEK[idx]}</span>
                  <span className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{date.getDate()}</span>
                </div>
              );
            })}
          </div>

          {/* Time slots */}
          {TIME_SLOTS.map(slot => {
            const hour = parseInt(slot.split(":")[0]);
            const isOffHours = hour < 9 || hour >= 19;
            return (
            <div key={slot} className="grid grid-cols-8 gap-0">
              <div className={`h-16 border border-border/30 flex items-center justify-center text-xs ${isOffHours ? "text-orange-400 bg-orange-500/5" : "text-muted-foreground bg-secondary/10"}`}>
                {slot}{isOffHours ? " ⚡" : ""}
              </div>
              {weekDates.map((date, idx) => {
                const dateKey = formatDateKey(date);
                const slotAppointments = getAppointmentsForSlot(dateKey, slot);
                const isWeekend = idx === 0 || idx === 6;
                return (
                  <div key={idx} className={`h-16 border border-border/30 p-0.5 overflow-hidden ${isWeekend ? 'bg-red-500/5' : isOffHours ? 'bg-orange-500/3' : ''}`}>
                    {slotAppointments.map(apt => renderAppointmentBadge(apt, true))}
                  </div>
                );
              })}
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Day View
  const renderDayView = () => {
    const dateKey = formatDateKey(currentDate);
    const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;

    return (
      <div className="space-y-1">
        <div className={`text-center py-4 rounded-lg mb-4 ${isWeekend ? 'bg-red-500/10 text-red-400' : 'bg-primary/10'}`}>
          <p className="text-lg font-medium">
            {DAYS_OF_WEEK_FULL[currentDate.getDay()]} {currentDate.getDate()} {MONTHS[currentDate.getMonth()]}
          </p>
          {isWeekend && <p className="text-sm">(Fermé)</p>}
        </div>

        {TIME_SLOTS.map(slot => {
          const hour = parseInt(slot.split(":")[0]);
          const isOffHours = hour < 9 || hour >= 19;
          const slotAppointments = getAppointmentsForSlot(dateKey, slot);
          return (
            <div key={slot} className="flex gap-4">
              <div className={`w-16 py-3 text-sm text-right ${isOffHours ? "text-orange-400" : "text-muted-foreground"}`}>
                {slot}{isOffHours ? " ⚡" : ""}
              </div>
              <div className={`flex-1 min-h-[60px] border border-border/30 rounded-lg p-2 ${isWeekend ? 'bg-red-500/5' : isOffHours ? 'bg-orange-500/5' : 'bg-card/50'}`}>
                <div className="space-y-2">
                  {slotAppointments.map(apt => renderAppointmentBadge(apt))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Get title based on view
  const getViewTitle = () => {
    if (view === "month") {
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (view === "week") {
      const weekDates = getWeekDates(currentDate);
      const start = weekDates[0];
      const end = weekDates[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()} - ${end.getDate()} ${MONTHS[start.getMonth()]} ${start.getFullYear()}`;
      }
      return `${start.getDate()} ${MONTHS[start.getMonth()].slice(0, 3)} - ${end.getDate()} ${MONTHS[end.getMonth()].slice(0, 3)} ${end.getFullYear()}`;
    } else {
      return `${currentDate.getDate()} ${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-chivo text-foreground flex items-center gap-2">
              <CalendarDays className="w-6 h-6 text-primary" />
              Rendez-vous
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Gérez les rendez-vous de prise en charge et de récupération
            </p>
          </div>
          <Button variant="outline" onClick={fetchAppointments} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{counts.pending}</p>
                  <p className="text-sm text-muted-foreground">À confirmer</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{counts.confirmed}</p>
                  <p className="text-sm text-muted-foreground">Confirmés</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{appointments.filter(a => a.type === "intake").length}</p>
                  <p className="text-sm text-muted-foreground">Prises en charge</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <Inbox className="w-5 h-5 text-cyan-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold">{appointments.filter(a => a.type === "pickup").length}</p>
                  <p className="text-sm text-muted-foreground">Récupérations</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters & View Toggle */}
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {/* Status filter */}
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-secondary/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="pending">À confirmer</SelectItem>
                <SelectItem value="confirmed">Confirmés</SelectItem>
              </SelectContent>
            </Select>

            {/* Type filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-44 bg-secondary/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="intake">Prise en charge</SelectItem>
                <SelectItem value="pickup">Récupération</SelectItem>
              </SelectContent>
            </Select>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Nom, téléphone, appareil..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-secondary/50 w-52"
              />
            </div>
          </div>

          {/* View toggle */}
          <div className="flex gap-1 bg-secondary/50 p-1 rounded-lg">
            <Button 
              variant={view === "month" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setView("month")}
            >
              Mois
            </Button>
            <Button 
              variant={view === "week" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setView("week")}
            >
              Semaine
            </Button>
            <Button 
              variant={view === "day" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setView("day")}
            >
              Jour
            </Button>
          </div>
        </div>

        {/* Calendar Navigation */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-4">
                <CardTitle className="text-lg">{getViewTitle()}</CardTitle>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Aujourd'hui
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate(1)}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {view === "month" && renderMonthView()}
                {view === "week" && renderWeekView()}
                {view === "day" && renderDayView()}
              </>
            )}
          </CardContent>
        </Card>

        {/* Pending Appointments List */}
        {counts.pending > 0 && (
          <Card className="bg-card border-border border-amber-500/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Rendez-vous à confirmer ({counts.pending})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredAppointments.filter(a => a.status === "pending").slice(0, 5).map(apt => {
                  const TypeIcon = TYPE_CONFIG[apt.type]?.icon || Calendar;
                  return (
                    <div key={apt.id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <TypeIcon className={`w-5 h-5 ${TYPE_CONFIG[apt.type]?.color}`} />
                        <div>
                          <p className="font-medium">{apt.client_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatLocalDate(apt.preferred_date, { day: "numeric", month: "long", year: "numeric" })} à {apt.preferred_time_slot}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(apt)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {apt.client_phone && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-green-500 text-green-500 hover:bg-green-500/10"
                            onClick={() => window.open(getWhatsAppLink(apt.client_phone, apt.client_name, formatLocalDate(apt.preferred_date, { day: "numeric", month: "long", year: "numeric" }), apt.preferred_time_slot, apt.type), "_blank")}
                            title="Envoyer rappel WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          className="bg-emerald-500 hover:bg-emerald-600"
                          onClick={() => handleStatusChange(apt, "confirmed")}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => handleStatusChange(apt, "cancelled")}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <div className="flex gap-4 text-xs text-muted-foreground flex-wrap">
          <div className="flex items-center gap-1">
            <Inbox className="w-4 h-4 text-cyan-500" />
            Prise en charge
          </div>
          <div className="flex items-center gap-1">
            <Truck className="w-4 h-4 text-emerald-500" />
            Récupération
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-500/20"></span>
            À confirmer
          </div>
          <div className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-emerald-500/20"></span>
            Confirmé
          </div>
        </div>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                Détails du rendez-vous
              </DialogTitle>
            </DialogHeader>
            {selectedAppointment && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {TYPE_CONFIG[selectedAppointment.type]?.icon && (
                      <span className={TYPE_CONFIG[selectedAppointment.type].color}>
                        {(() => { const Icon = TYPE_CONFIG[selectedAppointment.type].icon; return <Icon className="w-5 h-5" />; })()}
                      </span>
                    )}
                    <span className="font-medium">{TYPE_CONFIG[selectedAppointment.type]?.label}</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${STATUS_CONFIG[selectedAppointment.status]?.color}`}>
                    {STATUS_CONFIG[selectedAppointment.status]?.label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Client</p>
                    <p className="font-medium">{selectedAppointment.client_name}</p>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Téléphone</p>
                    <p>{selectedAppointment.client_phone}</p>
                  </div>
                </div>

                <div className="bg-secondary/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Date et heure</p>
                  <p className="font-medium">
                    {formatLocalDate(selectedAppointment.preferred_date, { weekday: "long", day: "numeric", month: "long" })}
                  </p>
                  <p className="text-muted-foreground">{selectedAppointment.preferred_time_slot}</p>
                </div>

                {selectedAppointment.device_info && (
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Appareil</p>
                    <p>{selectedAppointment.device_info}</p>
                  </div>
                )}

                {selectedAppointment.ticket_number && (
                  <div className="bg-secondary/30 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-muted-foreground">N° de ticket</p>
                    <p className="font-mono text-primary">{selectedAppointment.ticket_number}</p>
                    {selectedAppointment.repair_id && (
                      <button
                        onClick={() => { setDetailDialogOpen(false); routerNavigate(`/repairs/${selectedAppointment.repair_id}`); }}
                        className="w-full flex items-center justify-center gap-2 mt-1 py-1.5 px-3 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Voir la fiche réparation
                      </button>
                    )}
                  </div>
                )}

                {/* Badge statut converti/terminé */}
                {(selectedAppointment.status === "converted" || selectedAppointment.status === "completed") && !selectedAppointment.repair_id && (
                  <div className={`rounded-lg p-3 text-sm text-center ${selectedAppointment.status === "completed" ? "bg-teal-500/10 text-teal-400" : "bg-blue-500/10 text-blue-400"}`}>
                    {selectedAppointment.status === "converted" ? "✓ Convertie en réparation" : "✓ Réparation livrée"}
                  </div>
                )}

                {/* WhatsApp Reminder Button */}
                {selectedAppointment.client_phone && (
                  <Button 
                    variant="outline"
                    className="w-full border-green-500 text-green-500 hover:bg-green-500/10"
                    onClick={() => window.open(getWhatsAppLink(
                      selectedAppointment.client_phone, 
                      selectedAppointment.client_name, 
                      formatLocalDate(selectedAppointment.preferred_date, { day: "numeric", month: "long", year: "numeric" }), 
                      selectedAppointment.preferred_time_slot, 
                      selectedAppointment.type
                    ), "_blank")}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Envoyer rappel WhatsApp
                  </Button>
                )}

                {selectedAppointment.status === "pending" && (
                  <div className="flex gap-3 pt-4">
                    <Button 
                      className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                      onClick={() => handleStatusChange(selectedAppointment, "confirmed")}
                      disabled={updating}
                    >
                      {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirmer</>}
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 border-red-500 text-red-500 hover:bg-red-500/10"
                      onClick={() => handleStatusChange(selectedAppointment, "cancelled")}
                      disabled={updating}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Refuser
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
